import { select, takeEvery, put } from 'redux-saga/effects'
import { SET_IS_READY } from '@actions/types'
import { SET_PERMISSIONS } from '../actions/types'

const getAccountId = state => state.auth.accountId

const getHash = state => state.auth.hash

const getReadyData = state => state.initialize

/**
 * Checks whether tip reporting is enabled and tips exist for an account.
 * @param account
 * @returns {boolean} Whether tipping is enabled for the account.
 */
function tippingEnabledForAccount(account) {
  if (account?.tipping?.tips) {
    const { enable_tip_reports: enableTipReports, tips } = { ...account.tipping }

    const reportsEnabled = enableTipReports !== '0'
    const hasTips = tips?.length > 0

    return reportsEnabled && hasTips
  }

  return false
}

/**
 * Derives feature permissions from account config and dispatches them to the store.
 * @param action
 */
function * updatePermissions(action) {
  const accountId = yield select(getAccountId)

  const config = action.payload.data

  const account = config[accountId]

  const hasTipping = tippingEnabledForAccount(account)

  const hash = yield select(getHash)

  const hasEngage = account?.engage?.some(acct => acct.personal_access_code === hash.substring(0, 10))

  const hasResources = account?.resource_tab_enabled !== '0'

  const hasSafewalk = account?.safe_walk?.enable_safe_walk

  yield put({
    type: SET_PERMISSIONS,
    payload: {
      hasEngage,
      hasResources,
      hasTipping,
      hasSafewalk
    }
  })
}

/**
 * Recalculates permissions when a config Firestore document is modified.
 * @param action
 */
function * onDocumentModified(action) {
  const isConfig = action.meta.collection === 'config' && action.meta.doc !== null

  if (!isConfig) return

  yield updatePermissions(action)
}

/**
 * Handles initial Firestore listener response for config and user-config collections.
 * @param action
 */
function * onListenerResponse(action) {
  const isConfig = action.meta.collection === 'config' && action.meta.doc !== null
  const isUserConfig = action.meta.collection === 'user-config' && action.meta.doc !== null

  const { accountConfigIsReady, userConfigIsReady } = yield select(getReadyData)

  if (isUserConfig && !userConfigIsReady) {
    yield put({
      type: SET_IS_READY,
      payload: {
        userConfigIsReady: true
      }
    })
  }
  
  if (!isConfig) return

  yield updatePermissions(action)


  if (accountConfigIsReady) return

  yield put({
    type: SET_IS_READY,
    payload: {
      accountConfigIsReady: true
    }
  })
}

/**
 * Registers watchers for Firestore config document events.
 */
function * watchConfig() {
  yield takeEvery('@@reduxFirestore/DOCUMENT_MODIFIED', onDocumentModified)
  yield takeEvery('@@reduxFirestore/LISTENER_RESPONSE', onListenerResponse)
}

export default watchConfig
